<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar title="Véhicules" subtitle="Liste des véhicules enregistrés">
      <div class="btn-group mr-1" role="group" style="z-index: 2020">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les véhicules'"
          @click="exportVehicules"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <BaseButton
        class="btn btn-primary"
        v-tooltip="'Ajouter un véhicule'"
        @click="addNewVehicule"
        :loading="loading"
        icon="plus"
        text="Ajouter"
      ></BaseButton>
    </BaseToolBar>

    <!-- Filtres -->
    <div
      class="mb-4 d-flex justify-content-between align-items-center flex-wrap"
    >
      <div class="d-flex align-items-center mb-2 flex-wrap">
        <div class="btn-group mr-3" role="group">
          <button
            type="button"
            class="btn"
            :class="{
              'btn-primary': currentFilter === 'all',
              'btn-outline-primary': currentFilter !== 'all'
            }"
            @click="currentFilter = 'all'"
          >
            Tous
          </button>
          <button
            type="button"
            class="btn"
            :class="{
              'btn-primary': currentFilter === 'new',
              'btn-outline-primary': currentFilter !== 'new'
            }"
            @click="currentFilter = 'new'"
          >
            Nouveaux
            <span class="badge badge-light ml-1">{{ vehiculeNew.length }}</span>
          </button>
        </div>

        <!-- Filtre d'affectation -->
        <div class="form-group mb-0">
          <select
            class="form-control form-control-sm"
            v-model="affectationFilter"
            style="min-width: 150px;"
          >
            <option value="all">Tous</option>
            <option value="affected">Affectés</option>
            <option value="unaffected">Non affectés</option>
            <optgroup
              v-if="uniqueCollaborateurs.length > 0"
              label="Collaborateurs"
            >
              <option
                v-for="collab in uniqueCollaborateurs"
                :key="collab.id"
                :value="collab.id"
              >
                {{ collab.nom }} {{ collab.prenom }}
              </option>
            </optgroup>
          </select>
        </div>
      </div>
      <div class="btn-group mr-3 ml-3" role="group">
        <button
          type="button"
          class="btn"
          :class="{
            'btn-success': statutFilter === 'actif',
            'btn-outline-success': statutFilter !== 'actif'
          }"
          @click="statutFilter = 'actif'"
        >
          <i
            class="fas fa-circle text-success mr-1"
            style="font-size: 0.7rem;"
          ></i>
          Actifs
        </button>
        <button
          type="button"
          class="btn"
          :class="{
            'btn-danger': statutFilter === 'depasse',
            'btn-outline-danger': statutFilter !== 'depasse'
          }"
          @click="statutFilter = 'depasse'"
        >
          <i
            class="fas fa-circle text-danger mr-1"
            style="font-size: 0.7rem;"
          ></i>
          Restitution dépassée
        </button>
        <button
          type="button"
          class="btn"
          :class="{
            'btn-secondary': statutFilter === 'tous',
            'btn-outline-secondary': statutFilter !== 'tous'
          }"
          @click="statutFilter = 'tous'"
        >
          Tous
        </button>
      </div>
      <div class="input-group" style="max-width: 300px;">
        <div class="input-group-prepend">
          <span class="input-group-text">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <input
          type="text"
          class="form-control"
          placeholder="Rechercher..."
          v-model="searchQuery"
        />
      </div>
    </div>

    <!-- Tableau unique de véhicules -->
    <div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>Statut</th>
            <th>Immatriculation</th>
            <th>Modèle</th>
            <th>Catégorie</th>
            <th>
              Affecté à
              <i
                class="fas fa-filter ml-1 text-muted"
                style="cursor: pointer; font-size: 0.8rem;"
                @click="toggleAffectationFilter"
                v-tooltip="'Filtrer par affectation'"
              ></i>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="vehicule in filteredVehicules" :key="vehicule.id">
            <td>
              <div class="d-flex align-items-center">
                <span
                  class="status-dot mr-2"
                  :class="getVehiculeStatutClass(vehicule)"
                  v-tooltip="getVehiculeStatutTooltip(vehicule)"
                ></span>
                <span class="small">{{ getVehiculeStatutText(vehicule) }}</span>
              </div>
            </td>
            <td>{{ vehicule.immatriculation || "Non défini" }}</td>
            <td>{{ vehicule.modele || "Non défini" }}</td>
            <td>{{ getCategorieDescription(vehicule.categorie_id) }}</td>
            <td>
              <span
                :class="
                  vehicule.collaborateurs && vehicule.collaborateurs.length > 0
                    ? 'text-success'
                    : 'text-muted'
                "
              >
                {{ getAffectationInfo(vehicule) }}
                <span
                  v-if="
                    vehicule.collaborateurs &&
                      vehicule.collaborateurs.length > 1
                  "
                  class="badge badge-info ml-1"
                  v-tooltip="getMultipleAffectationsTooltip(vehicule)"
                >
                  +{{ vehicule.collaborateurs.length - 1 }}
                </span>
              </span>
            </td>
            <td>
              <router-link
                :to="'/vehicules/detail/' + vehicule.id"
                class="btn btn-sm btn-primary mr-2"
              >
                Détails
              </router-link>
              <button
                class="btn btn-sm btn-danger"
                @click="confirmDelete(vehicule)"
              >
                Supprimer
              </button>
            </td>
          </tr>
          <tr v-if="filteredVehicules.length === 0">
            <td colspan="6" class="text-center">
              Aucun véhicule ne correspond aux critères de recherche
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination (optionnelle) -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        <span class="text-muted">
          {{ filteredVehicules.length }} véhicule(s)
          <template v-if="statutFilter === 'actif'">actifs</template>
          <template v-else-if="statutFilter === 'depasse'"
            >avec restitution dépassée</template
          >
          <template v-else>au total</template>
          sur {{ vehicules.length }}
        </span>
      </div>
      <div
        v-if="
          affectationFilter !== 'all' || statutFilter !== 'actif' || searchQuery
        "
        class="text-right"
      >
        <button class="btn btn-sm btn-outline-secondary" @click="clearFilters">
          Effacer tous les filtres
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import { lastContratForCollab } from "@/helpers.js";

export default {
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      data: {},
      vehicules: [],
      loading: false,
      currentFilter: "all", // 'all' ou 'new'
      affectationFilter: "all", // 'all', 'affected', 'unaffected', ou ID du collaborateur
      searchQuery: "",
      statutFilter: "actif"
    };
  },
  methods: {
    getVehiculeStatutClass(vehicule) {
      if (!vehicule.date_restitution) {
        return "status-green"; // Vert si pas de date de restitution
      }

      const dateRestitution = this.$moment(vehicule.date_restitution);
      const today = this.$moment();

      if (dateRestitution.isBefore(today)) {
        return "status-red"; // Rouge si date de restitution passée
      }

      // Si la date de restitution approche (moins de 30 jours)
      if (dateRestitution.diff(today, "days") < 30) {
        return "status-orange"; // Orange pour les dates qui approchent
      }

      return "status-green"; // Vert par défaut pour les dates futures
    },

    getVehiculeStatutText(vehicule) {
      if (!vehicule.date_restitution) {
        return "Actif";
      }

      const dateRestitution = this.$moment(vehicule.date_restitution);
      const today = this.$moment();

      if (dateRestitution.isBefore(today)) {
        return "Restitution dépassée";
      }

      if (dateRestitution.diff(today, "days") < 30) {
        return "Restitution proche";
      }

      return "Actif";
    },

    getVehiculeStatutTooltip(vehicule) {
      if (!vehicule.date_restitution) {
        return "Véhicule actif - Pas de date de restitution programmée";
      }

      const dateRestitution = this.$moment(vehicule.date_restitution).format(
        "DD/MM/YYYY"
      );
      const today = this.$moment();

      if (this.$moment(vehicule.date_restitution).isBefore(today)) {
        return `Date de restitution dépassée (${dateRestitution})`;
      }

      return `À restituer le ${dateRestitution}`;
    },

    exportVehicules: function() {
      this.loading = true;
      this.$http
        .get("/vehicules-export", { responseType: "blob" })
        .then(response => {
          const blob = new Blob([response.data], {
            type: "text/csv;charset=utf-8;"
          });
          const link = document.createElement("a");
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", "export-vehicules.csv");
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .finally(() => (this.loading = false));
    },
    addNewVehicule() {
      this.loading = true;
      this.$http
        .post("/vehicules")
        .then(resp => {
          // Rediriger vers la page de détail du nouveau véhicule
          this.$router.push("/vehicules/detail/" + resp.data.id);
        })
        .catch(error => console.error(errHandler(error)))
        .finally(() => (this.loading = false));
    },
    confirmDelete(vehicule) {
      if (confirm("Êtes-vous sûr de vouloir supprimer ce véhicule ?")) {
        this.loading = true;
        this.$http
          .delete("/vehicules/" + vehicule.id)
          .then(() => {
            // Supprimer de la liste locale
            const index = this.vehicules.findIndex(x => x.id === vehicule.id);
            if (index !== -1) {
              this.vehicules.splice(index, 1);
            }
          })
          .catch(error => console.error(errHandler(error)))
          .finally(() => (this.loading = false));
      }
    },
    getCategorieDescription(categorieId) {
      if (!this.data || !this.data.categories) return "Non défini";
      const categorie = this.data.categories.find(
        cat => cat.id === categorieId
      );
      return categorie ? categorie.description : "Non défini";
    },
    getAffectationInfo(vehicule) {
      // Trouver le dernier collaborateur affecté
      if (!vehicule.collaborateurs || vehicule.collaborateurs.length === 0) {
        return "Non affecté";
      }

      const lastCollab = this.lastCollaborateur(vehicule);
      if (!lastCollab || !lastCollab.nom) {
        return "Problème affectation";
      }

      return `${lastCollab.nom} ${lastCollab.prenom || ""}`;
    },
    getMultipleAffectationsTooltip(vehicule) {
      if (!vehicule.collaborateurs || vehicule.collaborateurs.length <= 1) {
        return "";
      }

      // Prépare la liste des collaborateurs pour le tooltip (en excluant le principal)
      const mainCollabId = this.lastCollaborateur(vehicule).id;
      const otherCollabs = vehicule.collaborateurs
        .filter(
          collab =>
            collab.collaborateur &&
            collab.collaborateur.id !== mainCollabId &&
            collab.collaborateur.collaborateur_nom
        )
        .map(collab => {
          return {
            nom: collab.collaborateur.collaborateur_nom,
            prenom: collab.collaborateur.collaborateur_prenom || "",
            date: this.$moment(collab.date_affectation).format("DD/MM/YYYY")
          };
        })
        .sort((a, b) => a.nom.localeCompare(b.nom));

      // Format du tooltip
      if (otherCollabs.length === 0) {
        return "Aucune autre affectation";
      }

      return otherCollabs
        .map(collab => `${collab.nom} ${collab.prenom} <BR>`)
        .join("\n");
    },
    descriptionById(list, id) {
      if (!this.data || !this.data[list]) return "";
      const index = this.data[list].findIndex(x => x.id === id);
      if (this.data[list][index]) return this.data[list][index].description;
      else return "";
    },
    lastCollaborateur(vehicule) {
      const affectations = vehicule.collaborateurs.map(collab => {
        const lastContrat = lastContratForCollab(collab.collaborateur);
        if (
          collab &&
          collab.collaborateur &&
          collab.collaborateur.collaborateur_nom
        )
          return {
            id: collab.collaborateur.id,
            nom: collab.collaborateur.collaborateur_nom,
            prenom: collab.collaborateur.collaborateur_prenom,
            societe: lastContrat.activeVersion.contrat_siret
              ? lastContrat.activeVersion.contrat_siret.societe
              : "",
            dt: parseFloat(
              this.$moment(collab.date_affectation || "2000-01-01").format("X")
            ),
            dtFr: this.$moment(collab.date_affectation).format("DD/MM/YYYY"),
            restitution: this.$moment(collab.date_restitution).format(
              "DD/MM/YYYY"
            ),
            type: this.descriptionById(
              "categoriesAffectation",
              collab.categorie_id
            )
          };
      });
      let max = 0,
        reponse = {};
      if (affectations)
        affectations.forEach(affectation => {
          if (
            affectation &&
            affectation.dt &&
            affectation.dt > 0 &&
            max < affectation.dt
          ) {
            max = affectation.dt;
            reponse = affectation;
          }
        });
      return reponse;
    },
    toggleAffectationFilter() {
      // Ouvre le menu déroulant pour le filtre d'affectation
      // (peut être remplacé par une solution plus élégante avec un vrai dropdown personnalisé)
      this.affectationFilter =
        this.affectationFilter === "all" ? "affected" : "all";
    },
    clearFilters() {
      this.currentFilter = "all";
      this.affectationFilter = "all";
      this.searchQuery = "";
      this.statutFilter = "actif";
    }
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.$http.get("/vehicules"),
      this.$http.get("/vehicules-data")
    ])
      .then(([vehiculesResp, dataResp]) => {
        this.vehicules = vehiculesResp.data;
        this.data = dataResp.data;
      })
      .catch(error => console.error(errHandler(error)))
      .finally(() => (this.loading = false));
  },
  computed: {
    vehiculeNew() {
      return this.vehicules.filter(vehicule => vehicule.new === true);
    },
    allCollaborateurs() {
      // Collecte tous les collaborateurs à partir des véhicules
      const collaborateurs = [];
      this.vehicules.forEach(vehicule => {
        if (!vehicule.collaborateurs) return;

        vehicule.collaborateurs.forEach(collab => {
          if (
            collab &&
            collab.collaborateur &&
            collab.collaborateur.id &&
            collab.collaborateur.collaborateur_nom
          ) {
            collaborateurs.push({
              id: collab.collaborateur.id,
              nom: collab.collaborateur.collaborateur_nom,
              prenom: collab.collaborateur.collaborateur_prenom || ""
            });
          }
        });
      });
      return collaborateurs;
    },
    uniqueCollaborateurs() {
      // Élimine les doublons en utilisant un objet Map
      const unique = new Map();
      this.allCollaborateurs.forEach(collab => {
        if (!unique.has(collab.id)) {
          unique.set(collab.id, collab);
        }
      });

      // Conversion en tableau et tri par nom
      return Array.from(unique.values()).sort((a, b) =>
        a.nom.localeCompare(b.nom)
      );
    },
    filteredVehicules() {
      // Filtre par statut (nouveau ou tous)
      let result = this.vehicules;
      if (this.currentFilter === "new") {
        result = result.filter(vehicule => vehicule.new === true);
      }

      if (this.statutFilter === "actif") {
        result = result.filter(vehicule => {
          // Un véhicule est actif s'il n'a pas de date de restitution
          // ou si la date de restitution n'est pas dépassée
          if (!vehicule.date_restitution) return true;

          const dateRestitution = this.$moment(vehicule.date_restitution);
          const today = this.$moment();

          return dateRestitution.isAfter(today);
        });
      } else if (this.statutFilter === "depasse") {
        result = result.filter(vehicule => {
          // Un véhicule a sa restitution dépassée si la date est dans le passé
          if (!vehicule.date_restitution) return false;

          const dateRestitution = this.$moment(vehicule.date_restitution);
          const today = this.$moment();

          return dateRestitution.isBefore(today);
        });
      }

      // Filtre par affectation
      if (this.affectationFilter === "affected") {
        result = result.filter(
          vehicule =>
            vehicule.collaborateurs && vehicule.collaborateurs.length > 0
        );
      } else if (this.affectationFilter === "unaffected") {
        result = result.filter(
          vehicule =>
            !vehicule.collaborateurs || vehicule.collaborateurs.length === 0
        );
      } else if (this.affectationFilter !== "all") {
        // Filtre par ID de collaborateur spécifique
        result = result.filter(vehicule => {
          if (!vehicule.collaborateurs) return false;

          return vehicule.collaborateurs.some(
            collab =>
              collab.collaborateur &&
              collab.collaborateur.id == this.affectationFilter
          );
        });
      }

      // Filtre par recherche textuelle
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(vehicule => {
          return (
            (vehicule.immatriculation &&
              vehicule.immatriculation.toLowerCase().includes(query)) ||
            (vehicule.modele &&
              vehicule.modele.toLowerCase().includes(query)) ||
            this.getCategorieDescription(vehicule.categorie_id)
              .toLowerCase()
              .includes(query) ||
            this.getAffectationInfo(vehicule)
              .toLowerCase()
              .includes(query)
          );
        });
      }

      return result;
    }
  }
};
</script>

<style lang="css">
.table th {
  background-color: #f8f9fa;
}

.badge-primary {
  background-color: #007bff;
}

.badge-secondary {
  background-color: #6c757d;
}

.btn-group .btn {
  position: relative;
}

.btn-group .btn .badge {
  position: absolute;
  top: -8px;
  right: -8px;
  font-size: 0.65rem;
  border-radius: 50%;
  padding: 0.25rem 0.5rem;
}

.input-group .input-group-text {
  background-color: white;
  border-right: none;
}

.input-group .form-control {
  border-left: none;
}

.input-group .form-control:focus {
  box-shadow: none;
}

.text-success {
  color: #28a745;
  font-weight: 500;
}

.text-muted {
  color: #6c757d;
}

.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

.status-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-green {
  background-color: #28a745; /* Vert */
  box-shadow: 0 0 5px rgba(40, 167, 69, 0.5);
}

.status-red {
  background-color: #dc3545; /* Rouge */
  box-shadow: 0 0 5px rgba(220, 53, 69, 0.5);
}

.status-orange {
  background-color: #fd7e14; /* Orange */
  box-shadow: 0 0 5px rgba(253, 126, 20, 0.5);
}

.btn-outline-success {
  color: #28a745;
  border-color: #28a745;
}

.btn-outline-danger {
  color: #dc3545;
  border-color: #dc3545;
}

.btn-group .btn-outline-success:hover,
.btn-group .btn-success {
  background-color: #28a745;
  color: white;
}

.btn-group .btn-outline-danger:hover,
.btn-group .btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-group .btn-outline-secondary:hover,
.btn-group .btn-secondary {
  background-color: #6c757d;
  color: white;
}
</style>
